import React, { FC, useContext }            from 'react';
import _                                    from 'underscore';
import { Header, Icon, Label, List, Popup } from 'semantic-ui-react';
import { Cell, Grid }                       from 'styled-css-grid';

import { Align, CorvalCompletionState, CorvalProjectActivity, Uuid }        from '../../../../types';
import { DecisionEngineQueries }                                            from '../../../../queries';
import { TimeService }                                                      from '../../../../services';
import { ACTIVITY_CRITICALITY, ACTIVITY_CRITICALITY_INFO, ACTIVITY_STATUS } from '../../../../constants/ProductEnums';
import { DATES }                                                            from '../../../../constants/FormatStringEnums';
import { ItemFromQuery }                                                    from '../../../components/from-query/ItemFromQuery';
import { CollapsibleContentContainer, StyledContainer }                     from '../../../containers';
import { ProjectContext }                                                   from '../ProjectIndexPage';
import { FinePrintMessage }                                                 from '../../../components/util/messaging/FinePrintMessage';
import { useFeatureFlags }                                                  from '../../../../hooks';
import { ActivityCriticalityLabel }                                         from '../../../components/activities/ActivityCriticalityLabel';

interface ProjectActivityParentProgressDetailsProps {
	projectId: Uuid,
	projectActivityId: Uuid,
	triggeringStatuses?: ACTIVITY_STATUS[],
	
}

const defaultProps: ProjectActivityParentProgressDetailsProps = {
	projectId:          null,
	projectActivityId:  null,
	triggeringStatuses: [
		ACTIVITY_STATUS.NOTSTARTED,
		ACTIVITY_STATUS.INCOMPLETE
	],
}

/* Gets the a collection of parent activities that have a certain progression state.
* By default gets all parents still in progress or not started. Returns null if no
* parents are present within the triggering statuses prop.  */
const ProjectActivityParentProgressDetails: FC<ProjectActivityParentProgressDetailsProps> = ( props ) => {
	
	let projectContext = useContext( ProjectContext ),
	    projectId      = projectContext.projectId ?? props.projectId ?? null;
	
	if( !projectId || !props.projectActivityId ) return null;
	
	return (
		
		<ItemFromQuery
			query={ DecisionEngineQueries.queries.GET_PROJECT_ACTIVITIES( `
				id
				parentProjectActivities{
					id
					displayName
					startTimeUtc
					endTimeUtc
					completionState
					activity{
						id
						description
						defaultCriticality
						isRequired
					}
				}
			` ) }
			queryOptions={ {
				variables: {
					projectId,
					where: {
						id: {
							eq: props.projectActivityId
						}
					}
				}
			} }
			queryResultObject={ data => {
				
				let projectActivity = _.first( data.projectActivites ) as CorvalProjectActivity ?? null
				
				return ( projectActivity ) ? projectActivity.parentProjectActivities : [];
				
			} }>
			
			{ ( parents: CorvalProjectActivity[] ) => {
				
				/* Do analysis and trigger if needed */
				let triggeringParents = _.filter( parents, parent => ( props.triggeringStatuses.includes( parent.completionState as unknown as ACTIVITY_STATUS ) ) );
				
				if( !_.isEmpty( triggeringParents ) ){
					
					// Sort by start time
					let triggeringParentsSortedByTime = _.sortBy( triggeringParents, parent => TimeService.ISOtoFormat( parent.startTimeUtc, DATES.YEARMONTHDAY_MOMENT ) )
					
					return _.isFunction( props.children ) ? props.children( triggeringParentsSortedByTime ) : props.children;
				}
				
				return null;
			} }
		
		</ItemFromQuery>
	);
};

const IncompleteParentActivitiesWarning: FC<ProjectActivityParentProgressDetailsProps> = ( props ) => {
	
	let featureFlags = useFeatureFlags();
	
	return (
		
		<ProjectActivityParentProgressDetails
			{ ...props }
			triggeringStatuses={ [
				ACTIVITY_STATUS.NOTSTARTED,
				ACTIVITY_STATUS.INCOMPLETE,
			] }>
			
			{ ( parents: CorvalProjectActivity[] ) => {
				
				let parentCount          = parents.length,
				    multipleParents      = ( parentCount > 1 ),
				    parentCountStatement = multipleParents
				                           ? `are ${ parentCount } parent activities that are not`
				                           : `is ${ parentCount } parent activity that is not`;
				
				return (
					
					<Popup wide
					       position={ 'top center' }
					       content={ (
						       <StyledContainer textAlign={ Align.LEFT }>
							
							       <Header as={ 'h5' } icon={ 'clipboard check' } color={ 'orange' } content={ 'Parent Activities Incomplete' }/>
							       <FinePrintMessage>There { parentCountStatement } marked complete yet and is recommended to be completed first.</FinePrintMessage>
							
							       <List bulleted size={ 'small' }>
								       { parents.map( ( parent ) => {
									
									       let useCriticalityInfo  = featureFlags.enableProjectActivityCriticalityViewsAndTools,
									           criticality         = parent.activity.defaultCriticality,
									           criticalityInfo     = ACTIVITY_CRITICALITY_INFO[ criticality ],
									           criticalityLanguage = {
										           [ ACTIVITY_CRITICALITY.CRITICAL ]: 'This activity is vital for the project to be completed.',
										           [ ACTIVITY_CRITICALITY.PRIORITY ]: 'This actvity should be prioritized for completion.',
										           [ ACTIVITY_CRITICALITY.MODERATE ]: 'Your team should consider completing this activity as soon as possible.',
										           [ ACTIVITY_CRITICALITY.MINOR ]:    'Your team should consider completing this activity.',
									           },
									           criticalityLabel    = <ActivityCriticalityLabel value={ criticalityInfo.VALUE as ACTIVITY_CRITICALITY } size={ 'mini' }/>,
									           criticalityDesc     = useCriticalityInfo ? criticalityLanguage[ criticality ] : null,
									           requiredStatement   = parent.activity.isRequired ? 'Corval requires this activity to be present in your project.' : null,
									           signifiers          = _.compact( [ criticalityDesc, requiredStatement ] ).join( ' ' );
									
									       return (
										       <List.Item key={ parent.id }>
											       <Grid columns={ `auto auto` }>
												       <Cell><strong>{ parent.displayName }</strong> &mdash; { signifiers }</Cell>
												       <Cell>{ useCriticalityInfo ? criticalityLabel : null }</Cell>
											       </Grid>
										       </List.Item>
									       )
								       } ) }
							       </List>
						
						       </StyledContainer>
					       ) }
					       trigger={ (
						       <Label circular horizontal
						              color={ 'orange' }
						              size={ 'tiny' }
						              icon={ 'clipboard check' }
						              className={ 'cursor--pointer' }
						              content={ `${ parentCount } Status Conflict${ multipleParents ? 's' : '' }` }/>
					
					       ) }/>
				);
			} }
		</ProjectActivityParentProgressDetails>
	);
};

IncompleteParentActivitiesWarning.defaultProps    = defaultProps;
ProjectActivityParentProgressDetails.defaultProps = defaultProps;

export {
	IncompleteParentActivitiesWarning,
	ProjectActivityParentProgressDetails
}
